import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Users, Briefcase, MapPin, User, Loader2, ChevronLeft, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BarRow = ({ label, value, max, color = 'bg-blue-500' }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5 group">
      <span className="text-sm text-gray-700 w-44 truncate font-medium" title={label}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
        <div className={`${color} h-full rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 1.5)}%` }} />
      </div>
      <span className="text-sm font-bold text-gray-800 w-10 text-right">{value}</span>
    </div>
  );
};

const ReportSection = ({ title, data, color, icon, maxItems = 15 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data);
  const maxVal = Math.max(...entries.map(([, v]) => v));
  const visible = expanded ? entries : entries.slice(0, maxItems);
  const total = entries.reduce((s, [, v]) => s + v, 0);

  return (
    <Card className="p-5" data-testid={`report-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-bold text-sm text-gray-900">{title}</h3>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{entries.length} groups, {total} total</span>
        </div>
      </div>
      <div className="space-y-0.5">
        {visible.map(([label, value]) => (
          <BarRow key={label} label={label} value={value} max={maxVal} color={color} />
        ))}
      </div>
      {entries.length > maxItems && (
        <button onClick={() => setExpanded(!expanded)} className="mt-3 text-xs text-blue-600 hover:text-blue-800 font-medium">
          {expanded ? 'Show less' : `Show all ${entries.length} entries`}
        </button>
      )}
    </Card>
  );
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Card className={`p-4 ${color}`}>
    <p className="text-xs font-medium opacity-70">{label}</p>
    <p className="text-3xl font-bold">{value ?? 0}</p>
    {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
  </Card>
);

export default function ProfessionalReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/professionals/admin/reports`, { headers })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  const exportReportCSV = () => {
    if (!data) return;
    const rows = [['Section', 'Category', 'Count']];
    const addSection = (section, obj) => {
      if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([label, count]) => rows.push([section, label, count]));
      }
    };
    addSection('By Status', data.byStatus);
    addSection('By City', data.byCity);
    addSection('By State', data.byState);
    addSection('By Profession', data.byProfession);
    addSection('By Gender', data.byGender);
    addSection('By Nukh', data.byNukh);
    addSection('By Qualification', data.byQualification);
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `professional_reports_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) return <div className="p-6 text-center text-gray-500">No report data available.</div>;

  const statusCounts = data.byStatus || {};

  return (
    <div className="p-6" data-testid="professional-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => window.history.back()}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" /> Professional Directory Reports
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={exportReportCSV} data-testid="prof-reports-export">
          <Download className="w-4 h-4 mr-1" /> Export Report CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Professionals" value={data.total} color="bg-blue-50 text-blue-900" />
        <SummaryCard label="Approved" value={statusCounts['Approved'] || 0} color="bg-green-50 text-green-900" />
        <SummaryCard label="Pending" value={statusCounts['Pending'] || 0} color="bg-yellow-50 text-yellow-900" />
        <SummaryCard label="Rejected" value={statusCounts['Rejected'] || 0} color="bg-red-50 text-red-900" />
      </div>

      {/* Report Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ReportSection
          title="By Profession"
          data={data.byProfession}
          color="bg-blue-500"
          icon={<Briefcase className="w-4 h-4 text-blue-600" />}
        />
        <ReportSection
          title="By City"
          data={data.byCity}
          color="bg-emerald-500"
          icon={<MapPin className="w-4 h-4 text-emerald-600" />}
        />
        <ReportSection
          title="By State"
          data={data.byState}
          color="bg-violet-500"
          icon={<MapPin className="w-4 h-4 text-violet-600" />}
        />
        <ReportSection
          title="By Gender"
          data={data.byGender}
          color="bg-pink-500"
          icon={<User className="w-4 h-4 text-pink-600" />}
        />
        <ReportSection
          title="By Nukh"
          data={data.byNukh}
          color="bg-amber-500"
          icon={<Users className="w-4 h-4 text-amber-600" />}
        />
        <ReportSection
          title="By Status"
          data={data.byStatus}
          color="bg-cyan-500"
          icon={<BarChart3 className="w-4 h-4 text-cyan-600" />}
        />
        <ReportSection
          title="By Qualification"
          data={data.byQualification}
          color="bg-orange-500"
          icon={<BarChart3 className="w-4 h-4 text-orange-600" />}
          maxItems={10}
        />
      </div>
    </div>
  );
}
